import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  User,
  MapPin,
  Package,
  FileText,
  Upload,
  X,
  Plus,
  Trash2,
  ArrowLeft,
  Droplet,
  Building,
  Info,
  Search,
} from "lucide-react";
import ConfirmModal from "../../components/common/ConfirmModal";
import {
  fetchHospitals,
  fetchOrganizations,
  fetchWarehousesByOrganization,
  fetchBPartnerLocations,
  fetchProducts,
  fetchProductPrices,
  createBloodRequest,
  createOrderLine,
  uploadMultipleAttachments,
  fetchFreeBloodOrganization,
  STORAGE_KEYS,
} from "../../services/api";

const RH_FACTORS = [
  { value: "Positive", label: "Positive" },
  { value: "Negative", label: "Negative" },
];
const BLOOD_GROUPS = [
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "AB", label: "AB" },
  { value: "O", label: "O" },
];
const SEX_OPTIONS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "OT", label: "Other" },
];
const WARD_OPTIONS = [
  { value: "10000000", label: "Gynecology" },
  { value: "10000001", label: "Orthopedic" },
  { value: "10000002", label: "General Medicine" },
  { value: "10000003", label: "Maternity" },
  { value: "10000004", label: "Emergency Room" },
  { value: "10000005", label: "Other" },
];

const BloodRequestForm = () => {
  const navigate = useNavigate();
  const [isHospital, setIsHospital] = useState(false);
  const [isForPatient, setIsForPatient] = useState(false);
  const [formData, setFormData] = useState({
    AD_Org_ID: null,
    M_Warehouse_ID: null,
    C_BPartner_ID: null,
    C_BPartner_Location_ID: null,
    docstatus: "DR",
    docaction: "CO",
    totallines: 0,
    grandtotal: 0,
    PatientName: "",
    age: "",
    sex: "OT",
    Blood_group: "",
    rh_factor: "",
    MobileNo: "",
    C_BPartner_Hospital_ID: null,
    PatientID: "",
    ward: "10000005",
    BedNo: "-",
    TypeOfUrgency: "",
    IsSOTrx: "Y",
    x_match: "N",
    isRelatedOrg: "N",
    citizen_nid_birth_no: "",
    attachments: [],
    hospitalLetter: null,
    description: "",
  });

  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([
    { product: null, price: null, quantity: "1" },
  ]);
  const [productDialogVisible, setProductDialogVisible] = useState(false);
  const [attachmentDialogVisible, setAttachmentDialogVisible] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingProductData, setPendingProductData] = useState(null);
  const [showNoticeModal, setShowNoticeModal] = useState(true);

  // New state variables for free blood organizations
  const [freeBloodOrganizations, setFreeBloodOrganizations] = useState([]);
  const [selectedFreeBloodOrg, setSelectedFreeBloodOrg] = useState(null);
  const [freeBloodOrgSearch, setFreeBloodOrgSearch] = useState("");
  const [filteredFreeBloodOrgs, setFilteredFreeBloodOrgs] = useState([]);

  useEffect(() => {
    checkHospitalStatus();
    loadOrganizations();
  }, []);

  useEffect(() => {
    loadHospitals(isHospital);
  }, [isHospital]);

  // Load free blood organizations when notice modal is opened
  useEffect(() => {
    if (showNoticeModal) {
      loadFreeBloodOrganizations();
    }
  }, [showNoticeModal]);

  const checkHospitalStatus = async () => {
    try {
      const bpartnerData = localStorage.getItem(STORAGE_KEYS.B_PARTNER);
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (bpartnerData && userData) {
        const parsedData = JSON.parse(bpartnerData);
        const parsedUserData = JSON.parse(userData);
        const hospitalFlag =
          parsedData.isHospital === true || parsedData.isHospital === "true";
        setIsHospital(hospitalFlag);

        const updates = {};
        const businessPartnerLocations = await fetchBPartnerLocations(
          parsedData.id
        );
        if (businessPartnerLocations.length > 0) {
          updates.C_BPartner_Location_ID = businessPartnerLocations[0].id;
        }

        updates.C_BPartner_ID = parsedUserData.C_BPartner_ID.id;
        updates.C_BPartner_Hospital_ID = parsedUserData.C_BPartner_ID.id;
        updates.MobileNo = parsedData.Phone || "N/A"; // Ensure MobileNo has a value

        // Set default values for hidden fields to ensure payload completeness
        // if (parsedUserData.C_BPartner_ID && parsedUserData.C_BPartner_ID.id) {
        //   updates.PatientName = parsedUserData.C_BPartner_ID.id.toString();
        // } else {
        //   updates.PatientName = "N/A";
        // }

        // Set default values for other hidden fields
        updates.age = "0"; // Default age
        updates.sex = "OT"; // Default sex (Other)

        if (hospitalFlag) {
          updates.C_BPartner_Hospital_ID = parsedUserData.C_BPartner_ID.id;
          // updates.PatientName = parsedData.Name || parsedData.name || "N/A";
          updates.age = "0"; // Keep default age as 0
          updates.sex = "OT";
          updates.C_BPartner_ID = parsedUserData.C_BPartner_ID.id;
        }

        setFormData((prevData) => ({ ...prevData, ...updates }));
      }
    } catch (error) {
      console.error("Error checking hospital status:", error);
    }
  };

  const loadHospitals = async (isHospitalFlag) => {
    try {
      const data = await fetchHospitals(isHospitalFlag);
      if (data && Array.isArray(data)) {
        const formattedHospitals = data.map((hospital) => ({
          label: hospital.Name || hospital.name,
          value: hospital.id,
        }));
        setHospitals(formattedHospitals);
      } else {
        setHospitals([]);
      }
    } catch (error) {
      console.error("Error loading hospitals:", error);
      setHospitals([]);
    }
  };

  // New function to load free blood organizations
  const loadFreeBloodOrganizations = async () => {
    try {
      setLoading(true);
      const data = await fetchFreeBloodOrganization();
      console.log("Free blood organizations data:", data);

      if (data && Array.isArray(data)) {
        setFreeBloodOrganizations(data);
        setFilteredFreeBloodOrgs(data);
      } else {
        setFreeBloodOrganizations([]);
        setFilteredFreeBloodOrgs([]);
      }
    } catch (error) {
      console.error("Error loading free blood organizations:", error);
      setError("Failed to load free blood organizations: " + error.message);
      setFreeBloodOrganizations([]);
      setFilteredFreeBloodOrgs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const orgs = await fetchOrganizations();
      const formattedOrgs = orgs.map((org) => ({
        value: org.id,
        label: org.Name,
      }));
      setOrganizations(formattedOrgs);
    } catch (err) {
      setError("Failed to load organizations: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  const loadWarehouses = async () => {
    if (selectedOrganizationId) {
      try {
        setLoading(true);
        const fetchedWarehouses = await fetchWarehousesByOrganization(
          selectedOrganizationId
        );
        const formattedWarehouses = fetchedWarehouses.map((wh) => ({
          label: wh.name,
          value: wh.id,
        }));
        setWarehouses(formattedWarehouses);
        if (formattedWarehouses.length > 0) {
          const firstWarehouseId = formattedWarehouses[0].value;
          setSelectedWarehouseId(firstWarehouseId);
          setFormData((prev) => ({
            ...prev,
            M_Warehouse_ID: firstWarehouseId,
          }));
        } else {
          setSelectedWarehouseId(null);
          setFormData((prev) => ({ ...prev, M_Warehouse_ID: null }));
        }
      } catch (err) {
        setError("Failed to load warehouses: " + err.message);
        setWarehouses([]);
        setSelectedWarehouseId(null);
        setFormData((prev) => ({ ...prev, M_Warehouse_ID: null }));
      } finally {
        setLoading(false);
      }
    } else {
      setWarehouses([]);
      setSelectedWarehouseId(null);
      setFormData((prev) => ({ ...prev, M_Warehouse_ID: null }));
    }
  };

  // New function to handle search for free blood organizations
  const handleFreeBloodOrgSearch = (searchTerm) => {
    setFreeBloodOrgSearch(searchTerm);

    if (!searchTerm) {
      setFilteredFreeBloodOrgs(freeBloodOrganizations);
      return;
    }

    const filtered = freeBloodOrganizations.filter((org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredFreeBloodOrgs(filtered);
  };

  // New function to handle selection of free blood organization
  const handleFreeBloodOrgSelect = (org) => {
    setSelectedFreeBloodOrg(org);
    setFreeBloodOrgSearch(org ? org.name : "");
  };

  const validateForm = () => {
    const errors = {};

    // Validate patient fields if 'For Patient' is checked
    if (isForPatient) {
      if (!formData.PatientName)
        errors.PatientName = "Patient name is required";
      if (!formData.age) errors.age = "Age is required";
      if (!formData.sex) errors.sex = "Sex is required";
      if (!formData.MobileNo) errors.MobileNo = "Mobile number is required";
      if (!formData.ward) errors.ward = "Ward is required";
      if (!formData.PatientID) errors.PatientID = "Patient ID is required";
      if (!formData.BedNo) errors.BedNo = "Bed number is required";

      // Validate mobile number format if provided
      if (formData.MobileNo && !/^[0-9]{10,15}$/.test(formData.MobileNo)) {
        errors.MobileNo = "Please enter a valid mobile number";
      }

      // Validate age is a positive number if provided
      if (formData.age && (isNaN(formData.age) || formData.age <= 0)) {
        errors.age = "Please enter a valid age";
      }
    }

    // Validate visible required fields only
    if (!formData.Blood_group) errors.Blood_group = "Blood group is required";
    if (!formData.rh_factor) errors.rh_factor = "RH Factor is required";
    if (!isHospital && !formData.C_BPartner_Hospital_ID)
      errors.C_BPartner_Hospital_ID = "Hospital is required";
    if (!formData.TypeOfUrgency)
      errors.TypeOfUrgency = "Type of urgency is required";
    if (!formData.AD_Org_ID) errors.AD_Org_ID = "Organization is required";
    if (!formData.M_Warehouse_ID)
      errors.M_Warehouse_ID = "Warehouse is required";
    if (!formData.C_BPartner_Location_ID)
      errors.C_BPartner_Location_ID = "Location is required";
    if (!formData.hospitalLetter)
      errors.hospitalLetter = "Hospital letter is required";

    // Validate citizenship number if related organization is checked
    if (formData.isRelatedOrg === "Y" && !formData.citizen_nid_birth_no) {
      errors.citizen_nid_birth_no =
        "Citizenship No/Birth Certificate No is required";
    }

    // Debug log to check form data values
    console.log("Form validation - formData:", {
      PatientName: formData.PatientName,
      MobileNo: formData.MobileNo,
      age: formData.age,
      sex: formData.sex,
      PatientID: formData.PatientID,
      ward: formData.ward,
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const loadProducts = async () => {
    try {
      const data = await fetchProducts(1000001);
      const formattedProducts = data.map((product) => ({
        value: product.id,
        label: product.name,
        ...product,
      }));
      setProducts(formattedProducts);
    } catch (error) {
      console.error("Error loading products:", error);
      setError("Failed to load products");
    }
  };

  const handleSubmit = async () => {
    try {
      if (!validateForm()) {
        return;
      }
      setLoading(true);
      setError(null);
      await loadProducts();
      setProductDialogVisible(true);
    } catch (error) {
      console.error("Error loading products:", error);
      setError(error.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleAddLine = () => {
    setSelectedProducts((prev) => [
      ...prev,
      { product: null, price: null, quantity: "1" },
    ]);
  };

  const handleRemoveLine = (index) => {
    setSelectedProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProductSelect = async (product, lineIndex) => {
    try {
      const prices = await fetchProductPrices(product.value);
      const newSelectedProducts = [...selectedProducts];
      newSelectedProducts[lineIndex] = {
        ...newSelectedProducts[lineIndex],
        product: product,
        price: prices && prices.length > 0 ? prices[0] : null,
      };
      setSelectedProducts(newSelectedProducts);
    } catch (error) {
      console.error("Error fetching product prices:", error);
      setError("Failed to fetch product prices");
    }
  };

  const handleQuantityChange = (quantity, lineIndex) => {
    const newSelectedProducts = [...selectedProducts];
    newSelectedProducts[lineIndex] = {
      ...newSelectedProducts[lineIndex],
      quantity: quantity,
    };
    setSelectedProducts(newSelectedProducts);
  };

  const handleFilePick = (event) => {
    const files = Array.from(event.target.files);

    // Validate files before adding them
    const validFiles = [];
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
      "text/plain",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    for (const file of files) {
      console.log(
        `Validating file: ${file.name}, size: ${file.size}, type: ${file.type}`
      );

      if (file.size > maxFileSize) {
        setError(`File "${file.name}" is too large. Maximum size is 10MB.`);
        continue;
      }

      if (!allowedTypes.includes(file.type) && file.type !== "") {
        console.warn(
          `File type ${file.type} might not be supported for file: ${file.name}`
        );
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      console.log(`Adding ${validFiles.length} valid files to attachments`);
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...validFiles],
      }));
    }
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleHospitalLetterPick = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, hospitalLetter: file }));
    }
  };

  const handleRemoveHospitalLetter = () => {
    setFormData((prev) => ({ ...prev, hospitalLetter: null }));
  };

  const handleProductConfirm = async () => {
    try {
      const invalidLines = selectedProducts.some(
        (line) => !line.product || !line.price || !line.quantity
      );
      if (invalidLines) {
        setError("Please fill in all product details for each line");
        return;
      }

      setLoading(true);
      setError(null);

      // Prepare the order data and show confirmation modal
      const orderData = { ...formData };
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      const userDataJson = JSON.parse(userData);
      orderData.C_BPartner_ID = userDataJson.C_BPartner_ID.id;

      if (formData.isRelatedOrg === "Y") {
        orderData.related_org_ID = userDataJson.C_BPartner_ID.id;
        // Include citizen_nid_birth_no in the payload when related organization is checked
        orderData.citizen_nid_birth_no = formData.citizen_nid_birth_no;
      }

      // Convert hospital letter to base64 and include in payload
      if (formData.hospitalLetter) {
        const hospitalLetterBase64 = await convertFileToBase64(
          formData.hospitalLetter
        );
        orderData.hospitalLetter = {
          image: hospitalLetterBase64,
          filename: formData.hospitalLetter.name,
        };
      }

      // Store the prepared data and show confirmation modal
      setPendingProductData(orderData);
      setShowConfirmModal(true);
    } catch (error) {
      console.error("Error preparing blood request:", error);
      setError(error.message || "Failed to prepare blood request");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const handleConfirmBloodRequest = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create blood request with hospital letter included in payload
      const bloodRequestData = await createBloodRequest(pendingProductData);

      // Upload related organization attachments separately if they exist
      // if (formData.isRelatedOrg === "Y" && formData.attachments.length > 0) {
      //   try {
      //     console.log(
      //       `Attempting to upload ${formData.attachments.length} attachments for blood request ID: ${bloodRequestData.id}`
      //     );
      //     const uploadResults = await uploadMultipleAttachments(
      //       bloodRequestData.id,
      //       formData.attachments
      //     );
      //     console.log("All attachments uploaded successfully:", uploadResults);
      //   } catch (uploadError) {
      //     console.error("Detailed upload error:", uploadError);
      //     const errorMessage = uploadError.message || "Unknown upload error";
      //     setError(`File upload failed: ${errorMessage}`);
      //     setLoading(false);
      //     return;
      //   }
      // }

      // Get user data for order lines
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      const userDataJson = JSON.parse(userData);

      for (const line of selectedProducts) {
        const orderLinePayload = {
          AD_Org_ID: formData.AD_Org_ID,
          M_Warehouse_ID: formData.M_Warehouse_ID,
          C_BPartner_ID: userDataJson.C_BPartner_ID.id,
          C_Order_ID: bloodRequestData.id,
          C_Tax_ID: 1000000,
          M_Product_ID: line.product.value,
          Line: 10,
          DateOrdered: new Date().toISOString().split("T")[0] + "T00:00:00",
          C_Uom_ID: 100,
          QtyOrdered: parseInt(line.quantity),
          C_Currency_ID: 288,
          PriceList: line.price.priceList,
          PriceActual: line.price.priceList,
          PriceLimit: line.price.priceList,
          LineNetAmt: line.price.priceList * parseInt(line.quantity),
          FreightAmt: 0,
          QtyEntered: parseInt(line.quantity),
          PriceEntered: line.price.priceList,
        };
        await createOrderLine(orderLinePayload);
      }

      setProductDialogVisible(false);
      setPendingProductData(null);
      setSuccess(true);
      setTimeout(() => navigate("/blood-requests"), 2000);
    } catch (error) {
      console.error(error);
      setError(
        error.message || "Failed to create blood request and order lines"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Back Button */}
      <div className="flex items-center mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <Droplet className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              New Blood Request
            </h1>
          </div>
          <p className="text-gray-600 text-sm sm:text-base">
            Create a new blood request for patients
          </p>
        </div>
        <button
          onClick={() => setShowNoticeModal(true)}
          className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 shadow-sm"
        >
          <Info className="h-4 w-4 mr-2" />
          <span className="text-sm font-medium">Free Blood Info</span>
        </button>
      </div>

      {/* Patient Information */}
      <div className="card p-2 sm:p-4 md:p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Building className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Chapter Information
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Chapter *
            </label>
            <Select
              value={organizations.find(
                (org) => org.value === selectedOrganizationId
              )}
              onChange={async (option) => {
                const orgId = option ? option.value : null;
                setSelectedOrganizationId(orgId);
                setFormData((prev) => ({
                  ...prev,
                  AD_Org_ID: orgId,
                }));

                if (orgId) {
                  setLoading(true);
                  try {
                    const fetchedWarehouses =
                      await fetchWarehousesByOrganization(orgId);
                    const formattedWarehouses = fetchedWarehouses.map((wh) => ({
                      label: wh.name,
                      value: wh.id,
                    }));
                    setWarehouses(formattedWarehouses);
                    if (formattedWarehouses.length > 0) {
                      const firstWarehouseId = formattedWarehouses[0].value;
                      setSelectedWarehouseId(firstWarehouseId);
                      setFormData((prev) => ({
                        ...prev,
                        M_Warehouse_ID: firstWarehouseId,
                      }));
                    } else {
                      setSelectedWarehouseId(null);
                      setFormData((prev) => ({
                        ...prev,
                        M_Warehouse_ID: null,
                      }));
                    }
                  } catch (err) {
                    setError("Failed to load warehouses: " + err.message);
                    setWarehouses([]);
                    setSelectedWarehouseId(null);
                    setFormData((prev) => ({ ...prev, M_Warehouse_ID: null }));
                  } finally {
                    setLoading(false);
                  }
                } else {
                  setWarehouses([]);
                  setSelectedWarehouseId(null);
                  setFormData((prev) => ({ ...prev, M_Warehouse_ID: null }));
                }
              }}
              options={organizations}
              placeholder="Select Chapter"
              classNamePrefix="react-select"
              isClearable={false}
              isSearchable={true}
              className={validationErrors.AD_Org_ID ? "border-red-500" : ""}
            />
            {validationErrors.AD_Org_ID && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.AD_Org_ID}
              </p>
            )}
          </div>

          {!isHospital && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hospital *
              </label>
              <Select
                value={hospitals.find(
                  (hospital) =>
                    hospital.value === formData.C_BPartner_Hospital_ID
                )}
                onChange={(option) =>
                  setFormData({
                    ...formData,
                    C_BPartner_Hospital_ID: option ? option.value : null,
                  })
                }
                options={hospitals}
                placeholder="Select Hospital"
                classNamePrefix="react-select"
                isClearable={false}
                isSearchable={true}
                className={
                  validationErrors.C_BPartner_Hospital_ID
                    ? "border-red-500"
                    : ""
                }
              />
              {validationErrors.C_BPartner_Hospital_ID && (
                <p className="text-red-500 text-sm mt-1">
                  {validationErrors.C_BPartner_Hospital_ID}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Patient Information */}
      <div className="card p-2 sm:p-4 md:p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <User className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Patient Information
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isForPatient"
              checked={isForPatient}
              onChange={(e) => {
                setIsForPatient(e.target.checked);
                if (!e.target.checked) {
                  // Reset patient fields when unchecking
                  setFormData((prev) => ({
                    ...prev,
                    PatientName: "",
                    age: "",
                    sex: "OT",
                    MobileNo: "",
                    ward: "10000005",
                    PatientID: "",
                    BedNo: "-",
                  }));
                }
              }}
              className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
            />
            <label
              htmlFor="isForPatient"
              className="text-sm font-medium text-gray-700"
            >
              For Patient
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isForPatient && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient Name *
                </label>
                <input
                  type="text"
                  value={formData.PatientName}
                  onChange={(e) =>
                    setFormData({ ...formData, PatientName: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    validationErrors.PatientName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter patient name"
                />
                {validationErrors.PatientName && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.PatientName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age *
                </label>
                <input
                  type="number"
                  value={formData.age}
                  onChange={(e) =>
                    setFormData({ ...formData, age: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    validationErrors.age ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter age"
                />
                {validationErrors.age && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.age}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sex *
                </label>
                <Select
                  value={SEX_OPTIONS.find(
                    (option) => option.value === formData.sex
                  )}
                  onChange={(option) =>
                    setFormData({
                      ...formData,
                      sex: option ? option.value : "",
                    })
                  }
                  options={SEX_OPTIONS}
                  placeholder="Select Sex"
                  classNamePrefix="react-select"
                  isClearable={false}
                  isSearchable={false}
                  className={validationErrors.sex ? "border-red-500" : ""}
                />
                {validationErrors.sex && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.sex}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile No *
                </label>
                <input
                  type="tel"
                  value={formData.MobileNo}
                  onChange={(e) =>
                    setFormData({ ...formData, MobileNo: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    validationErrors.MobileNo
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter mobile number"
                />
                {validationErrors.MobileNo && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.MobileNo}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ward *
                </label>
                <Select
                  value={WARD_OPTIONS.find(
                    (ward) => ward.value === formData.ward
                  )}
                  onChange={(option) =>
                    setFormData({
                      ...formData,
                      ward: option ? option.value : "",
                    })
                  }
                  options={WARD_OPTIONS}
                  placeholder="Select Ward"
                  classNamePrefix="react-select"
                  isClearable={false}
                  isSearchable={false}
                  className={validationErrors.ward ? "border-red-500" : ""}
                />
                {validationErrors.ward && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.ward}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Patient ID *
                </label>
                <input
                  type="text"
                  value={formData.PatientID}
                  onChange={(e) =>
                    setFormData({ ...formData, PatientID: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    validationErrors.PatientID
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter patient ID"
                />
                {validationErrors.PatientID && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.PatientID}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bed No *
                </label>
                <input
                  type="text"
                  value={formData.BedNo}
                  onChange={(e) =>
                    setFormData({ ...formData, BedNo: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                    validationErrors.BedNo
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter bed number"
                />
                {validationErrors.BedNo && (
                  <p className="text-red-500 text-sm mt-1">
                    {validationErrors.BedNo}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Age is hidden */}
          {/*
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Age *
            </label>
            <input
              type="number"
              value={formData.age}
              onChange={(e) =>
                setFormData({ ...formData, age: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                validationErrors.age ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter age"
            />
            {validationErrors.age && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.age}
              </p>
            )}
          </div>
          */}

          {/* Sex is hidden */}
          {/*
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sex *
            </label>
            <Select
              value={SEX_OPTIONS.find(
                (option) => option.value === formData.sex
              )}
              onChange={(option) =>
                setFormData({ ...formData, sex: option ? option.value : "" })
              }
              options={SEX_OPTIONS}
              placeholder="Select Sex"
              classNamePrefix="react-select"
              isClearable={false}
              isSearchable={false}
              className={validationErrors.sex ? "border-red-500" : ""}
            />
            {validationErrors.sex && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.sex}
              </p>
            )}
          </div>
          */}

          {/* MobileNo is hidden */}
          {/*
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mobile No *
            </label>
            <input
              type="tel"
              value={formData.MobileNo}
              onChange={(e) =>
                setFormData({ ...formData, MobileNo: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                validationErrors.MobileNo ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter mobile number"
            />
            {validationErrors.MobileNo && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.MobileNo}
              </p>
            )}
          </div>
          */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blood Group *
            </label>
            <Select
              value={BLOOD_GROUPS.find(
                (bg) => bg.value === formData.Blood_group
              )}
              onChange={(option) =>
                setFormData({
                  ...formData,
                  Blood_group: option ? option.value : "",
                })
              }
              options={BLOOD_GROUPS}
              placeholder="Select Blood Group"
              classNamePrefix="react-select"
              isClearable={false}
              isSearchable={false}
              className={validationErrors.Blood_group ? "border-red-500" : ""}
            />
            {validationErrors.Blood_group && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.Blood_group}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              RH Factor *
            </label>
            <Select
              value={RH_FACTORS.find(
                (factor) => factor.value === formData.rh_factor
              )}
              onChange={(option) =>
                setFormData({
                  ...formData,
                  rh_factor: option ? option.value : "",
                })
              }
              options={RH_FACTORS}
              placeholder="Select RH Factor"
              classNamePrefix="react-select"
              isClearable={false}
              isSearchable={false}
              className={validationErrors.rh_factor ? "border-red-500" : ""}
            />
            {validationErrors.rh_factor && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.rh_factor}
              </p>
            )}
          </div>
          {/* Ward is hidden */}
          {/*
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ward *
            </label>
            <Select
              value={WARD_OPTIONS.find((ward) => ward.value === formData.ward)}
              onChange={(option) =>
                setFormData({ ...formData, ward: option ? option.value : "" })
              }
              options={WARD_OPTIONS}
              placeholder="Select Ward"
              classNamePrefix="react-select"
              isClearable={false}
              isSearchable={false}
              className={validationErrors.ward ? "border-red-500" : ""}
            />
            {validationErrors.ward && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.ward}
              </p>
            )}
          </div>
          */}

          {/* PatientID is hidden */}
          {/*
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient ID *
            </label>
            <input
              type="text"
              value={formData.PatientID}
              onChange={(e) =>
                setFormData({ ...formData, PatientID: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                validationErrors.PatientID
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="Enter patient ID"
            />
            {validationErrors.PatientID && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.PatientID}
              </p>
            )}
          </div>
          */}

          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bed No *
            </label>
            <input
              type="text"
              value={formData.BedNo}
              onChange={(e) =>
                setFormData({ ...formData, BedNo: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                validationErrors.BedNo ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter bed number"
            />
            {validationErrors.BedNo && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.BedNo}
              </p>
            )}
          </div> */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type of Urgency *
            </label>
            <input
              type="text"
              value={formData.TypeOfUrgency}
              onChange={(e) =>
                setFormData({ ...formData, TypeOfUrgency: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                validationErrors.TypeOfUrgency
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="Enter type of urgency"
            />
            {validationErrors.TypeOfUrgency && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.TypeOfUrgency}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Needed CrossMatch?
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="Y"
                checked={formData.x_match === "Y"}
                onChange={(e) =>
                  setFormData({ ...formData, x_match: e.target.value })
                }
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="N"
                checked={formData.x_match === "N"}
                onChange={(e) =>
                  setFormData({ ...formData, x_match: e.target.value })
                }
                className="mr-2"
              />
              No
            </label>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter description"
          />
        </div>
      </div>

      {/* Related Organization
      <div className="card p-2 sm:p-4 md:p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Droplet className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Free Blood Eligibility
          </h2>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Is the Patient Eligible For Free Blood?
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="Y"
                checked={formData.isRelatedOrg === "Y"}
                onChange={(e) =>
                  setFormData({ ...formData, isRelatedOrg: e.target.value })
                }
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="N"
                checked={formData.isRelatedOrg === "N"}
                onChange={(e) =>
                  setFormData({ ...formData, isRelatedOrg: e.target.value })
                }
                className="mr-2"
              />
              No
            </label>
          </div>
        </div>

        {formData.isRelatedOrg === "Y" && (
          <div className="mt-4 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Citizenship No/Birth Certificate No *
            </label>
            <input
              type="text"
              maxLength={20}
              value={formData.citizen_nid_birth_no || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  citizen_nid_birth_no: e.target.value,
                })
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                validationErrors.citizen_nid_birth_no
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              placeholder="Enter citizenship or birth certificate number"
            />
            {validationErrors.citizen_nid_birth_no && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.citizenshipNumber}
              </p>
            )}
          </div>
        )}

        {formData.isRelatedOrg === "Y" && (
          <div>
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Required Documents:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-2">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Recommendation Letter</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Residence Related Information</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Citizenship / Marriage / Birth Certificate</span>
                </li>
              </ul>
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachments ({formData.attachments.length})
            </label>
            <button
              type="button"
              onClick={() => setAttachmentDialogVisible(true)}
              className="btn-secondary flex items-center"
            >
              <Upload className="h-4 w-4 mr-2" />
              Manage Attachments
            </button>
          </div>
        )}
      </div> */}

      {/* Hospital Letter */}
      <div className="card p-2 sm:p-4 md:p-6">
        <div className="flex items-center space-x-2 mb-4">
          <FileText className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            Blood Requisition Form Issued By Hospital *
          </h2>
        </div>

        <div className="mb-4">
          {formData.hospitalLetter ? (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">
                  {formData.hospitalLetter.name}
                </p>
                <p className="text-sm text-gray-500">
                  {(formData.hospitalLetter.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                type="button"
                onClick={handleRemoveHospitalLetter}
                className="text-red-600 hover:text-red-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="text-center p-6 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500">No Requisition Form selected</p>
              <p className="text-sm text-gray-400">
                Click "Upload Requisition Form" to select a photo
              </p>
            </div>
          )}

          <input
            type="file"
            onChange={handleHospitalLetterPick}
            className="hidden"
            id="hospital-letter-input"
          />
          <label
            htmlFor="hospital-letter-input"
            className={`btn-secondary flex items-center justify-center mt-2 ${
              validationErrors.hospitalLetter ? "border-red-500" : ""
            }`}
          >
            <Upload className="h-4 w-4 mr-2" />
            {formData.hospitalLetter
              ? "Change Hospital Letter"
              : "Upload Hospital Letter"}
          </label>
          {validationErrors.hospitalLetter && (
            <p className="text-red-500 text-sm mt-1">
              {validationErrors.hospitalLetter}
            </p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="btn-primary flex items-center"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <Package className="h-4 w-4 mr-2" />
          )}
          Add Blood Products
        </button>
      </div>

      {/* Product Selection Dialog */}
      {productDialogVisible &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
            }}
          >
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-200">
              <div className="flex items-center justify-between mb-6 pb-2 border-b border-gray-100">
                <h2 className="text-2xl font-bold text-primary-700">
                  Select Products
                </h2>
                <button
                  onClick={() => setProductDialogVisible(false)}
                  className="text-gray-400 hover:text-gray-700 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-7 w-7" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SN
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedProducts.map((line, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="min-w-[200px]">
                              <Select
                                value={products.find(
                                  (product) =>
                                    product.value === line.product?.value
                                )}
                                onChange={(option) => {
                                  const product = option ? option : null;
                                  handleProductSelect(product, index);
                                }}
                                options={products}
                                placeholder="Select Product"
                                classNamePrefix="react-select"
                                isClearable={false}
                                isSearchable={true}
                                className="w-full"
                                menuPortalTarget={
                                  typeof window !== "undefined"
                                    ? document.body
                                    : null
                                }
                                menuPosition="fixed"
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 99999,
                                  }),
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "38px",
                                    fontSize: "14px",
                                  }),
                                }}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              value={line.quantity}
                              onChange={(e) =>
                                handleQuantityChange(e.target.value, index)
                              }
                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                              placeholder="1"
                              min="1"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {selectedProducts.length > 1 && (
                              <button
                                onClick={() => handleRemoveLine(index)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                aria-label="Remove Line"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  onClick={handleAddLine}
                  disabled={selectedProducts.some((line) => !line.product)}
                  className="btn-secondary flex items-center px-4 py-2 rounded-lg shadow-sm hover:bg-primary-50 transition-colors"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Another Line
                </button>
              </div>

              <div className="flex justify-end space-x-4 mt-8">
                <button
                  onClick={() => setProductDialogVisible(false)}
                  className="btn-secondary px-6 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProductConfirm}
                  disabled={selectedProducts.some(
                    (line) => !line.product || !line.price || !line.quantity
                  )}
                  className="btn-primary px-6 py-2 rounded-lg"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Attachment Dialog */}
      {attachmentDialogVisible &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Manage Attachments</h2>
                <button
                  onClick={() => setAttachmentDialogVisible(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {formData.attachments.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No files selected</p>
                    <p className="text-sm text-gray-400">
                      Click "Add Files" to select attachments
                    </p>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-medium mb-2">
                      Selected Files ({formData.attachments.length}):
                    </h3>
                    <div className="space-y-2">
                      {formData.attachments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-gray-900">
                              {file.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setAttachmentDialogVisible(false)}
                  className="btn-secondary"
                >
                  Okay
                </button>
                <input
                  type="file"
                  multiple
                  onChange={handleFilePick}
                  className="hidden"
                  id="attachment-input"
                />
                <label
                  htmlFor="attachment-input"
                  className="btn-primary flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Files
                </label>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Success Message */}
      {success &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 text-center">
              <div className="text-green-500 text-6xl mb-4">✓</div>
              <h2 className="text-xl font-semibold mb-2">Success!</h2>
              <p className="text-gray-600">
                Blood request created successfully
              </p>
            </div>
          </div>,
          document.body
        )}

      {/* Error Message */}
      {error &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="text-red-500 text-6xl mb-4">✗</div>
              <h2 className="text-xl font-semibold mb-2">Error</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => setError(null)}
                className="btn-primary w-full"
              >
                Dismiss
              </button>
            </div>
          </div>,
          document.body
        )}

      {/* Notice Modal */}
      {showNoticeModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="text-center mb-4 bg-red-600 text-white p-4 rounded-t-lg -m-6 mb-6">
                <h2 className="text-2xl font-semibold mb-2">
                  Notice Regarding Organization Providing Free Blood
                </h2>
              </div>
              <div className="text-gray-600 mb-6">
                <div className="bg-blue-50 border-l-4 border-red-400 p-4 mb-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Info className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-600 justify-center">
                        <strong>Important:</strong> Patients who are permanent
                        residents of the provinces or local levels listed below
                        are eligible for free blood upon submission of original
                        required documents.
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FileText className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-600 justify-center">
                        <strong>Required Documents:</strong>
                        <li>
                          Blood Requisition / Request Form Issued By Hospital
                        </li>
                        <li>Proof Of Permanent Residency</li>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Organization Search Section */}
                <div className="mt-6">
                  <div className="flex mb-3">
                    <div className="flex-shrink-0">
                      <Search className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-600 font-semibold">
                        Search Organization
                      </p>
                    </div>
                  </div>

                  <div className="ml-8">
                    <div className="relative mb-3">
                      <Select
                        value={
                          selectedFreeBloodOrg
                            ? {
                                value: selectedFreeBloodOrg.c_bpartner_id,
                                label: selectedFreeBloodOrg.name,
                              }
                            : null
                        }
                        onChange={(option) => {
                          if (option) {
                            const org = freeBloodOrganizations.find(
                              (o) => o.c_bpartner_id === option.value
                            );
                            handleFreeBloodOrgSelect(org);
                          } else {
                            handleFreeBloodOrgSelect(null);
                          }
                        }}
                        options={filteredFreeBloodOrgs.map((org) => ({
                          value: org.c_bpartner_id,
                          label: org.name,
                        }))}
                        onInputChange={handleFreeBloodOrgSearch}
                        placeholder="Type organization name..."
                        classNamePrefix="react-select"
                        isClearable={true}
                        isSearchable={true}
                        className="w-full"
                        menuPortalTarget={
                          typeof window !== "undefined" ? document.body : null
                        }
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                          control: (base) => ({ ...base, minHeight: "42px" }),
                        }}
                      />
                    </div>

                    {/* Display hospitals if organization is selected and has hospitals */}
                    {selectedFreeBloodOrg &&
                      selectedFreeBloodOrg.hospitals &&
                      selectedFreeBloodOrg.hospitals.length > 0 && (
                        <div className="mt-6 bg-blue-50 rounded-lg border border-blue-200 p-5">
                          <div className="flex items-center mb-4">
                            <Building className="h-5 w-5 text-red-600 mr-2" />
                            <h4 className="text-lg font-semibold text-red-600">
                              Hospitals in {selectedFreeBloodOrg.name}
                            </h4>
                          </div>
                          <p className="text-sm text-red-600 mb-4">
                            The following hospitals in this organization provide
                            free blood services:
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {selectedFreeBloodOrg.hospitals.map(
                              (hospital, index) => (
                                <div
                                  key={hospital.hospital_details_id || index}
                                  className="flex items-start p-3 bg-white rounded-lg border border-blue-100 shadow-sm hover:shadow-md transition-shadow duration-200"
                                >
                                  <Building className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <h5 className="font-medium text-gray-800 text-sm">
                                      {hospital.name}
                                    </h5>
                                    {/* <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                                      Free Blood Available
                                    </span> */}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                    {/* Message when organization has no hospitals */}
                    {selectedFreeBloodOrg &&
                      selectedFreeBloodOrg.hospitals &&
                      selectedFreeBloodOrg.hospitals.length === 0 && (
                        <div className="mt-6 bg-yellow-50 rounded-lg border border-yellow-200 p-5">
                          <div className="flex">
                            <Info className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0" />
                            <div>
                              <h4 className="text-lg font-medium text-yellow-800">
                                Notice
                              </h4>
                              <p className="text-sm text-yellow-700 mt-1">
                                You're eligible for free blood services if you
                                are a resident of {selectedFreeBloodOrg.name}.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                    {/* Message when no organizations match search */}
                    {freeBloodOrgSearch &&
                      filteredFreeBloodOrgs.length === 0 && (
                        <div className="mt-6 bg-gray-50 rounded-lg border border-gray-200 p-5">
                          <div className="text-center">
                            <Search className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <h4 className="text-lg font-medium text-gray-700">
                              No Organizations Found
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              No organizations match your search term "
                              {freeBloodOrgSearch}". Please try a different
                              search term.
                            </p>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowNoticeModal(false)}
                  className="btn-primary px-6 py-2"
                >
                  Okay
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Confirmation Modal - Higher z-index to appear above product modal */}
      {showConfirmModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[999999] overflow-y-auto">
            <ConfirmModal
              isOpen={showConfirmModal}
              onClose={() => setShowConfirmModal(false)}
              onConfirm={handleConfirmBloodRequest}
              title="Create Blood Request"
              message="Are you sure you want to create this blood request? This action cannot be undone."
              confirmText="Create Request"
              cancelText="Cancel"
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default BloodRequestForm;
